import type { ReactChild } from 'react';
import { useReducer, useEffect, useState, useMemo, useRef } from 'react';

import { Button } from '@capsule/consumer-design-system';

import { useMountEffect } from '@react-hookz/web';
import classNames from 'classnames';
import assign from 'lodash/assign';
import isEmpty from 'lodash/isEmpty';
import pluralize from 'pluralize';
import { Link } from 'react-router-dom';
import { css } from 'styled-components';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { bffCheckoutCartUrl } from 'src/app/client/api';
import { Card, CardItem } from 'src/app/components/Elements/Card';
import LinkToCardItem from 'src/app/components/Elements/Card/LinkToCardItem';
import { StylizedCardItemLinkWithIcon } from 'src/app/components/Elements/Card/StylizedCardItemLinkWithIcon';
import { RadioGroup } from 'src/app/components/Elements/Inputs';
import { TextUs } from 'src/app/components/Elements/Location/TextUs';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import FixedFloat from 'src/app/components/Elements/Page/Standard/FixedFloat';
import Spinner from 'src/app/components/Elements/Spinner';
import { Typography } from 'src/app/components/Elements/Typography';
import GenericError from 'src/app/components/Error/Generic';
import { CheckBoxInputUnwired } from 'src/app/components/Form/CheckBoxInput';
import { CheckoutTransferModal } from 'src/app/components/Modal/Checkout';
import IdVerificationRequired from 'src/app/components/Pages/Checkout/IdVerificationRequired/container';
import { useOrderCount } from 'src/app/hooks/data/order';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import useExperiments from 'src/app/hooks/useExperiments';
import { useLazySegmentTrack } from 'src/app/hooks/useLazySegmentTrack';
import { extractValidationErrors } from 'src/app/popsicle/utils';
import * as paths from 'src/app/router/paths';
import { hipaa, privacy, termsAndConditions } from 'src/app/router/paths';
import * as actionTypes from 'src/app/state/actions';
import { fetchOrderHistory } from 'src/app/state/actions';
import { updateCartTip } from 'src/app/state/actions/cart';
import {
  getAccount,
  getAccountIsFetching,
  getBFFCartDeliveryMethodsIsFetching,
  getCheckoutPickupStationsIsFetching,
  getDeliveryAddresses,
  getHasFetchedOrders,
  getHasSelectedPickupStationAsDeliveryMethod,
  getIsBffCartFetching,
  getIsFetchingBFFOrders,
  getIsFetchingDeliveryAddresses,
} from 'src/app/state/selectors';
import { openChat } from 'src/app/state/slices/chat';
import { useSelector, useDispatch } from 'src/app/state/store';
import { extractErrorCodes } from 'src/app/utils';
import {
  API_ERROR_CODES,
  BFF_ERROR_CODES,
  CAPSULE_ICON_STRINGS,
  DeliveryMethodLabels,
  DeliveryMethodType,
  ESI_MAIL_KEY,
  GENERIC_REFRIGERATION_MESSAGE,
  ID_UPLOAD_STATUS,
  IS_MOBILE,
} from 'src/consts';
import { CHECKOUT_ADD_PHOTO_ID_BUTTON } from 'src/consts/testIDs';

import { RefigerationBanner } from '../../Banner/Banners';
import { CalloutBox } from '../../Elements/CalloutBox';

import type { PropsFromRedux } from './container';
import { CheckoutPriceBreakdownCard } from './PriceBreakdownCard';
import { CheckoutDeliveryMethodSection, CheckoutPickupSection } from './Sections';
import { StyledLink } from './styles';
import styles from './styles.module.scss';
import {
  convertNumberToValueInCents,
  getUITextForDeliveryAddress,
  getUITextForDeliveryMethod,
  getUITextForPaymentMethod,
} from './utils';

const uploadIdMessages = {
  [ID_UPLOAD_STATUS.unverified]: 'Please add a photo ID to complete this order',
  [ID_UPLOAD_STATUS.uploaded]: 'Photo ID added. Pending review.',
  [ID_UPLOAD_STATUS.pending]: 'Photo ID added. Pending review.',
  [ID_UPLOAD_STATUS.failed]: 'Please add a photo ID to complete this order',
  [ID_UPLOAD_STATUS.verified]: 'Photo ID added. Pending review.',
};

function ContactUsLink() {
  const dispatch = useDispatch();

  const dispatchOpenChat = () => dispatch(openChat());

  const handleClick = e => {
    e.preventDefault();
    dispatchOpenChat();
  };

  if (IS_MOBILE) {
    return <TextUs>contact us</TextUs>;
  } else {
    return (
      <a href="#" onClick={handleClick}>
        contact us
      </a>
    );
  }
}

const Checkout = ({
  accountId,
  accountSetupRequired,
  allergyProfileComplete,
  bffCartDeliveryAddress,
  bffCartDeliveryMethod,
  bffCartPaymentMethod,
  cartId,
  cartItems,
  currentUser,
  dispatchLoadAccount,
  dispatchAddressListClicked,
  dispatchCheckoutCart,
  dispatchClearPickupStations,
  dispatchCloseCheckoutTransferModal,
  dispatchCompleteOrder,
  dispatchDeliveryWindowsListClicked,
  dispatchLoadCartDeliveryWindows,
  dispatchPatchTOS,
  dispatchPaymentListClicked,
  dispatchPlaceOrderButtonClicked,
  dispatchProcessOrderFailure,
  dispatchRequiresAccount,
  dispatchSetPickupStationAsDeliveryMethod,
  dispatchSetSelectedDeliveryMethodLabelFromCheckout,
  dispatchSignatureRequirementChecked,
  dispatchTrackSubmitOrder,
  dispatchLoadDeliveryAddresses,
  dispatchFetchCart,
  dispatchCreateCart,
  dispatchGetPickupStations,
  dispatchLoadCartDeliveryMethods,
  hasMultiplePatients,
  idVerificationRequired,
  idVerificationStatus,
  isAccountSet,
  hasSelectedCourierAsDeliveryMethod,
  isEligibleForPickups,
  isRefrigerationRequired,
  isUserESIMailEligible,
  orderCompletable,
  otcItemsLength,
  patientMedicationIds,
  pickupStationsLength,
  prescriptionFulfillmentIds,
  shouldShowDoormanText,
  instructions,
  showCheckoutTransferModal,
  showPrice,
  shouldShowTermsCallout,
  subtotal,
  tip,
  total,
  totalQuantity,
  segmentTrack,
}: PropsFromRedux) => {
  const [selectedDeliveryMethodFromRadioGroup, setSelectedDeliveryMethod] = useState(DeliveryMethodLabels.freeDelivery);
  const hasSelectedPickupStationFromRadioGroup =
    selectedDeliveryMethodFromRadioGroup === DeliveryMethodLabels.pickUpAtWork;

  const dispatch = useDispatch();
  const isFetchingAccount = useSelector(getAccountIsFetching);
  const isFetchingBffCart = useSelector(getIsBffCartFetching);
  const isFetchingOrders = useSelector(getIsFetchingBFFOrders);
  const isFetchingDeliveryAddresses = useSelector(getIsFetchingDeliveryAddresses);
  const isFetchingDeliveryMethods = useSelector(getBFFCartDeliveryMethodsIsFetching);
  const isFetchingCheckoutPickupStations = useSelector(getCheckoutPickupStationsIsFetching);
  const account = useSelector(getAccount);
  const hasFetchedOrders = useSelector(getHasFetchedOrders);
  const deliveryAddresses = useSelector(getDeliveryAddresses);
  const hasSelectedPickupStationAsDeliveryMethod = useSelector(getHasSelectedPickupStationAsDeliveryMethod);

  const { data: orderCount } = useOrderCount();
  const hasNoPriorOrders = useMemo(() => orderCount === 0, [orderCount]);
  /*
   * HACK ALERT: I'm setting redux state with this data because it's so hard-baked into the checkout flow step
   * management in `pushNextCheckoutState` that I can't easily refactor it to just use the hook. I've removed
   * the dependency on this redux data in favor of the hook everywhere else I could.
   *
   * Historically the state for this was set when calling BFF for Optimizely feature flag data. I'm clearing
   * this as a part of cleaning up our dependencies on Optimizely, which we are removing in favor of Amplitude.
   */
  useEffect(() => {
    dispatch({
      type: actionTypes.SET_NUMBER_OF_ORDERS,
      numberOfOrders: orderCount,
    });
  }, [dispatch, orderCount]);

  // If tip is null from the bff cart that means the user is ineligible and we do not show tips.
  const isTippingEnabled = tip !== null && tip !== undefined;
  const [isUserEditingTip, setIsUserEditingTip] = useState(false);
  const [shouldFinishEditingTip, setShouldFinishEditingTip] = useState(false);
  const [previousTipValueForAnalytics, setPreviousTipValueForAnalytics] = useState<number | null>(null);

  const handleOnEditClicked = () => {
    setPreviousTipValueForAnalytics(convertNumberToValueInCents(tip));
    segmentTrack(UNIFIED.viewTip, { starting_value: convertNumberToValueInCents(tip) });
  };

  const handleOnTipSubmitted = async (tip: number) => {
    segmentTrack(UNIFIED.editTip, {
      starting_value: previousTipValueForAnalytics,
      final_value: tip,
    });
    await dispatch(updateCartTip({ tip }));
  };

  useMountEffect(() => {
    if (!isFetchingAccount && !account) {
      dispatchLoadAccount();
    }

    if (!isFetchingDeliveryAddresses && !deliveryAddresses) {
      dispatchLoadDeliveryAddresses();
    }

    if (cartId) {
      dispatchFetchCart(cartId);
    } else {
      dispatchCreateCart(accountId);
    }

    if (currentUser && !isAccountSet && !accountSetupRequired) {
      dispatchRequiresAccount();
    }

    if (accountId && bffCartDeliveryAddress?.id && cartId && !isFetchingDeliveryMethods) {
      dispatchLoadCartDeliveryMethods(accountId);
    }

    if (cartId && !isFetchingCheckoutPickupStations) {
      dispatchGetPickupStations();
    }
  });

  useEffect(() => {
    if (!hasFetchedOrders && !isFetchingOrders) {
      dispatch(fetchOrderHistory());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFetchedOrders, isFetchingOrders]);

  useEffect(() => {
    dispatchSetSelectedDeliveryMethodLabelFromCheckout(selectedDeliveryMethodFromRadioGroup);
    if (selectedDeliveryMethodFromRadioGroup === DeliveryMethodLabels.freeDelivery && isEligibleForPickups) {
      dispatchLoadCartDeliveryWindows();
    }
  }, [
    dispatchLoadCartDeliveryWindows,
    dispatchSetSelectedDeliveryMethodLabelFromCheckout,
    isEligibleForPickups,
    selectedDeliveryMethodFromRadioGroup,
  ]);

  const { key, formattedDeliveryDate } = bffCartDeliveryMethod || {};

  const trackSubmitOrderPayload = {
    display_name_primary: cartItems.map(({ product }) => product.name).join(', '),
    first_time_order: hasNoPriorOrders,
    cart_id: cartId,
    order_id: 'None',
    prescription_fulfillment_ids: prescriptionFulfillmentIds,
    patientmedication_ids: patientMedicationIds,
    has_dependents: hasMultiplePatients,
    subtotal: convertNumberToValueInCents(subtotal),
    tip_amount: convertNumberToValueInCents(tip),
    otc_count: otcItemsLength,
    item_count: prescriptionFulfillmentIds?.length,
    delivery_method: key === ESI_MAIL_KEY ? `${key} (${formattedDeliveryDate})` : key,
    total: convertNumberToValueInCents(total),
  };

  const [trackOrderSuccessful] = useLazySegmentTrack(UNIFIED.orderSuccessful);
  const [trackOrderFailed] = useLazySegmentTrack(UNIFIED.orderFailed);
  const [trackOrderError] = useLazySegmentTrack(UNIFIED.error);

  const addressPath = paths.checkoutSelectAddress.toPath({ cartId });
  const cardPath = paths.checkoutSelectCreditCard.toPath({ cartId });

  const uploadIdMessage = uploadIdMessages[idVerificationStatus];

  const [isSubmitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: ReactChild; _generic: ReactChild } | null>(null);
  const [hasError, setHasError] = useState(false);
  const errorRef = useRef<HTMLDivElement>();
  useEffect(() => {
    if (errorRef?.current?.scrollIntoView) {
      errorRef.current?.scrollIntoView();
    }
  }, [errors]);

  const [signatureRequirementChecked, toggleSignatureCheck] = useReducer(s => !s, false);

  const showSignatureOrPickupRequirement =
    bffCartDeliveryMethod?.key === DeliveryMethodType.courier ||
    bffCartDeliveryMethod?.key === DeliveryMethodType.pickupStation;

  const completeOrder = async e => {
    e.preventDefault();
    if (!orderCompletable || (showSignatureOrPickupRequirement && !signatureRequirementChecked) || isUserEditingTip) {
      setHasError(true);
      if (isUserEditingTip) {
        setShouldFinishEditingTip(true);
      }
    } else {
      dispatchPlaceOrderButtonClicked({ cartId });
      if (hasNoPriorOrders) segmentTrack(UNIFIED.placeFirstRxOrder);
      dispatchTrackSubmitOrder(trackSubmitOrderPayload);
      setSubmitting(true);

      try {
        // TODO: fill in delivery method id
        const response = await dispatchCheckoutCart({
          cartId,
        });
        const completedOrderId = response.id;

        const trackOrderSuccessfulPayload = assign({}, trackSubmitOrderPayload, {
          order_id: completedOrderId,
          has_dependents: hasMultiplePatients,
        });
        trackOrderSuccessful(trackOrderSuccessfulPayload);
        dispatchClearPickupStations();
        dispatchCompleteOrder(completedOrderId);

        if (orderCount !== undefined) {
          window.analytics.identify({ orders_placed: orderCount + 1 });
        }

        // Only update account's TOS if the TOS callout is shown
        if (shouldShowTermsCallout) {
          dispatchPatchTOS(accountId);
        }
      } catch (error) {
        const bffError = error.error;
        let analyticsErrorSummary = '';
        let paymentProcessingErrorDetails;
        if (bffError?.errorCode) {
          // BFF error format
          const errorCode = bffError?.errorCode;
          const message = `${bffError?.title}. ${bffError?.flavorText}`;
          setErrors({
            _generic: <>{message}</>,
          });
          analyticsErrorSummary = message;
          if (BFF_ERROR_CODES[errorCode] === 'payment_processing_error') {
            paymentProcessingErrorDetails = bffError.details;
          }
        } else {
          // Pharmakon error format
          const errorCodes = extractErrorCodes(error);
          const allErrors = extractValidationErrors(error);

          if (errorCodes[API_ERROR_CODES.payment_processing_error]) {
            paymentProcessingErrorDetails = allErrors.data;
          }

          // TODO(spayson): improve these comments
          if (allErrors._generic) {
            // generic errors get rendered

            const errors = {
              _generic: `We couldn’t place your order. ${allErrors._generic}`,
            };
            analyticsErrorSummary = errors._generic;

            setErrors(errors);
          } else if (allErrors.cart ?? allErrors.order) {
            // this is likely because the order already exists

            setErrors({
              // anything else in this situation indicates we have a problem that requires support
              _generic: (
                <>
                  It appears your order is already placed. Please check your{' '}
                  <Link to={paths.orders.toPath()}>order history</Link>, or <ContactUsLink />.
                </>
              ),
            });

            analyticsErrorSummary =
              'It appears your order is already placed. Please check your order history, or contact us.';
          } else if (allErrors.delivery_window) {
            // this is likely because the delivery window is no longer available
            const message =
              'The delivery window you selected is no longer available. Please select another window and place your order.';
            setErrors({
              _generic: <>{message}</>,
            });
            analyticsErrorSummary = message;
          } else {
            setErrors({
              // anything else in this situation indicates we have a problem that requires support
              _generic: (
                <>
                  There was a problem processing your order. Please try again, or <ContactUsLink />.
                </>
              ),
            });
            analyticsErrorSummary = 'There was a problem processing your order.';
          }
        }

        if (paymentProcessingErrorDetails) {
          dispatchProcessOrderFailure({
            detail: paymentProcessingErrorDetails,
            accountId,
            cartId,
            has_dependents: hasMultiplePatients,
          });
        }

        const trackOrderFailurePayload = assign({}, trackSubmitOrderPayload, {
          error_code: null,
          error_summary: analyticsErrorSummary,
        });

        const trackErrorPayload = {
          errorMessage: 'Checkout Failed',
          errorSummary: analyticsErrorSummary,
          api: bffCheckoutCartUrl(cartId),
          errorCode: extractErrorCodes(error),
          source: window.location.href,
        };

        trackOrderFailed(trackOrderFailurePayload);
        trackOrderError(trackErrorPayload);
        setSubmitting(false);
      }
    }
  };

  const hasChosenDeliveryAddressButNotDeliveryMethod = bffCartDeliveryAddress && !bffCartDeliveryMethod;
  const hasDeliveryAddressAndDeliveryMethodBeenSet = !!bffCartDeliveryAddress && !!bffCartDeliveryMethod;
  const [hasAlreadyChosenCourierDeliveryAddress, setHasAlreadyChosenCourierDeliveryAddress] = useState(false);

  const { firstRowText: deliveryMethodFirstRowText, secondRowText: deliveryMethodSecondRowText } =
    getUITextForDeliveryMethod(bffCartDeliveryMethod, hasChosenDeliveryAddressButNotDeliveryMethod);

  const { firstRowText: deliveryAddressFirstRowText, secondRowText: deliveryAddressSecondRowText } =
    getUITextForDeliveryAddress(bffCartDeliveryAddress);

  const { firstRowText: paymentMethodFirstRowText, secondRowText: paymentMethodSecondRowText } =
    getUITextForPaymentMethod(bffCartPaymentMethod, hasDeliveryAddressAndDeliveryMethodBeenSet);

  const handleSelectDeliveryMethod = (selectedLabel: DeliveryMethodLabels) => {
    dispatchSetSelectedDeliveryMethodLabelFromCheckout(selectedLabel);
    if (
      selectedLabel === DeliveryMethodLabels.pickUpAtWork &&
      !isEmpty(bffCartDeliveryAddress) &&
      (bffCartDeliveryMethod?.key === DeliveryMethodType.courier || bffCartDeliveryMethod?.key == null)
    ) {
      setHasAlreadyChosenCourierDeliveryAddress(true);
    } else {
      setHasAlreadyChosenCourierDeliveryAddress(false);
    }

    setSelectedDeliveryMethod(selectedLabel);
    if (
      selectedLabel === DeliveryMethodLabels.pickUpAtWork &&
      !hasSelectedPickupStationAsDeliveryMethod &&
      pickupStationsLength === 1
    ) {
      dispatchSetPickupStationAsDeliveryMethod();
    }
  };
  const { loggedInUser, isLoggedIn } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', isLoggedIn);

  const [shouldRequireSignature, setShouldRequireSignature] = useState(true);
  const [isLoadingShouldRequireSignature, setLoadingShouldRequireSignature] = useState(true);
  const { fetchVariant } = useExperiments();
  useEffect(() => {
    if (!!user) {
      const evaluateShouldNotRequireSignature = async () => {
        const variant = await fetchVariant({
          experimentKey: 'should_not_require_signature',
          userId: user.accountId,
        });
        // The feature flag is "should *NOT* require signature". And our state is
        // "shouldRequireSignature". So we want the opposite of the variant.
        setShouldRequireSignature(variant !== 'on');
        setLoadingShouldRequireSignature(false);
      };

      evaluateShouldNotRequireSignature();
    }
  }, [fetchVariant, user]);

  const hasChosenDeliveryAfterSettingDeliveryMethodAsPickupStation =
    hasSelectedPickupStationAsDeliveryMethod &&
    selectedDeliveryMethodFromRadioGroup === DeliveryMethodLabels.freeDelivery;

  const hasChosenPickupStationAfterSettingDeliveryMethodAsCourier =
    hasSelectedCourierAsDeliveryMethod && selectedDeliveryMethodFromRadioGroup === DeliveryMethodLabels.pickUpAtWork;

  const signatureRequirementTitle = hasSelectedPickupStationFromRadioGroup
    ? 'PICKUP REQUIREMENT'
    : shouldRequireSignature
    ? 'SIGNATURE REQUIREMENT'
    : 'DELIVERY REQUIREMENT';

  const fieldValidity = {
    signature: signatureRequirementChecked,
  };

  const validationErrors = {
    signature: `• Select ${signatureRequirementTitle}`,
  };

  const checkoutPickupSectionProps = {
    bffCartDeliveryAddress,
    cartId,
    deliveryMethodFirstRowText,
    deliveryMethodSecondRowText,
    dispatchAddressListClicked,
    dispatchDeliveryWindowsListClicked,
    selectedDeliveryMethodFromRadioGroup,
    hasChosenPickupStationAfterSettingDeliveryMethodAsCourier,
    hasAlreadyChosenCourierDeliveryAddress,
    hasError,
  };

  const checkoutDeliveryMethodSectionProps = {
    ...checkoutPickupSectionProps,
    addressPath,
    bffCartDeliveryMethod,
    deliveryAddressFirstRowText,
    deliveryAddressSecondRowText,
    isUserESIMailEligible,
    hasChosenDeliveryAfterSettingDeliveryMethodAsPickupStation,
    hasChosenDeliveryAddressButNotDeliveryMethod,
    shouldShowDoormanText,
    instructions,
  };

  // If the bffCart is fetching and the pickupStation has not been selected load the spinner,
  // If the bffCart is fetching and the pickupStation has been selected, do not load a spinner,
  // as this will re-render the page with a new isFetching API call and reset the pickupStation data in redux
  const hasBFFCartFetched = isFetchingBffCart && !hasSelectedPickupStationFromRadioGroup;
  const isInitialDataFetched =
    isFetchingAccount ||
    isFetchingOrders ||
    hasBFFCartFetched ||
    isFetchingDeliveryAddresses ||
    isFetchingDeliveryMethods ||
    isFetchingCheckoutPickupStations;

  useEffect(() => {
    if (!isInitialDataFetched) {
      segmentTrack(UNIFIED.viewCheckout, {
        first_time_order: hasNoPriorOrders,
        patientmedication_ids: patientMedicationIds,
        subtotal: convertNumberToValueInCents(subtotal),
        tip_amount: convertNumberToValueInCents(tip),
        total: convertNumberToValueInCents(total),
        cart_id: cartId,
        has_dependents: hasMultiplePatients,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialDataFetched]);

  const cartHasZeroDollarTotal = total === 0.0;

  if (isInitialDataFetched || isLoadingShouldRequireSignature) {
    return (
      <StandardPage title={'Checkout'}>
        <Spinner centered />
      </StandardPage>
    );
  }

  return (
    <StandardPage title={'Checkout'}>
      <div className={styles.component}>
        <div ref={errorRef}>
          <GenericError errors={errors} />
        </div>
        <IdVerificationRequired />
        <CheckoutTransferModal
          showModal={showCheckoutTransferModal}
          handleButtonClickAndExit={dispatchCloseCheckoutTransferModal}
          buttonText="Continue To Checkout"
          title="You're all set."
          bodyText="We're working on your transfer now and we will text you updates along the way."
        />
        {shouldShowTermsCallout && (
          <CalloutBox>
            <Typography variant="small">
              Capsule's{' '}
              <StyledLink to={termsAndConditions.toPath()} target="_blank">
                Terms of Service
              </StyledLink>
              ,{' '}
              <StyledLink to={privacy.toPath()} target="_blank">
                Privacy Policy
              </StyledLink>
              , and{' '}
              <StyledLink to={hipaa.toPath()} target="_blank">
                HIPAA Policy
              </StyledLink>{' '}
              have been updated. By placing an order, you agree to the updates.
            </Typography>
          </CalloutBox>
        )}
        <Card>
          {isRefrigerationRequired && <RefigerationBanner titleText={GENERIC_REFRIGERATION_MESSAGE} />}
          {isEligibleForPickups && (
            <CardItem>
              <RadioGroup
                labelList={[DeliveryMethodLabels.freeDelivery, DeliveryMethodLabels.pickUpAtWork]}
                defaultSelectedLabel={
                  hasSelectedPickupStationAsDeliveryMethod
                    ? DeliveryMethodLabels.pickUpAtWork
                    : DeliveryMethodLabels.freeDelivery
                }
                onClick={handleSelectDeliveryMethod}
                ariaLabel={'Choose Delivery Method Group'}
              />
            </CardItem>
          )}
          {hasSelectedPickupStationFromRadioGroup ? (
            <>
              <CheckoutPickupSection {...checkoutPickupSectionProps} />
            </>
          ) : (
            <>
              <CheckoutDeliveryMethodSection {...checkoutDeliveryMethodSectionProps} />
            </>
          )}
          <StylizedCardItemLinkWithIcon
            sectionTitle={
              // Do not show the error text when there is a zero dollar subtotal because the consumer does not have to input
              // a payment method
              hasError && isEmpty(bffCartPaymentMethod) && !cartHasZeroDollarTotal ? '• Missing Payment' : 'Payment'
            }
            capsuleIcon={CAPSULE_ICON_STRINGS.checkout_payment}
            to={cardPath}
            ariaLabel={'Choose or Add a Credit Card'}
            handleOnClick={dispatchPaymentListClicked}
            dataCy={'payment'}
            textList={[paymentMethodFirstRowText, paymentMethodSecondRowText]}
            // We disable the credit card section when either the delivery address or delivery method are null/empty.
            // Also it is disabled when there is a zero dollar subtotal/copay as there is no need for a payment method.
            // We do not disable when the cart payment method is filled in from the shouldPrefill api call
            isDisabled={
              ((isEmpty(bffCartDeliveryAddress) || isEmpty(bffCartDeliveryMethod)) && isEmpty(bffCartPaymentMethod)) ||
              cartHasZeroDollarTotal
            }
            disabledText={'Add credit card'}
            hasError={hasError && isEmpty(bffCartPaymentMethod)}
          />
          {hasNoPriorOrders && !allergyProfileComplete && (
            <StylizedCardItemLinkWithIcon
              sectionTitle="Profile"
              capsuleIcon={CAPSULE_ICON_STRINGS.checkout_allergy}
              to={paths.checkoutAllergyProfile.toPath({ cartId })}
              ariaLabel="Add Allergies"
              textList={[`Allergies, medical conditions, and other meds ${allergyProfileComplete ? 'added' : ''}`]}
              isDisabled={!bffCartDeliveryAddress && !allergyProfileComplete}
              disabledText={`Allergies, medical conditions, and other meds ${allergyProfileComplete ? 'added' : ''}`}
            />
          )}
          {uploadIdMessage && idVerificationRequired && (
            <StylizedCardItemLinkWithIcon
              sectionTitle={
                hasError &&
                (idVerificationStatus === ID_UPLOAD_STATUS.failed ||
                  idVerificationStatus === ID_UPLOAD_STATUS.unverified)
                  ? '• Photo Id Required'
                  : 'Photo Id Required'
              }
              capsuleIcon={CAPSULE_ICON_STRINGS.checkout_photo}
              to={paths.checkoutAddIdentification.toPath({ cartId })}
              ariaLabel={'Add Photo ID'}
              data-testid={CHECKOUT_ADD_PHOTO_ID_BUTTON}
              textList={[uploadIdMessage]}
              isDisabled={!bffCartDeliveryAddress}
              disabledText={uploadIdMessage}
            />
          )}

          <LinkToCardItem to={paths.checkoutCartItems.toPath({ cartId })} aria-label={'Order Item Purchase Summary'}>
            <div className={styles.orderItems} data-cy="order-item">
              <span className={classNames('label', styles.itemCount)}>
                {`${totalQuantity} ${pluralize('Item', totalQuantity)}`}
              </span>
            </div>
          </LinkToCardItem>

          {/**
           * Instead of just deleting this code, it's commented out so the actual user's flow is more readily obvious
           * to anybody returning to this area of the codeabase. Basically, if `accountSetupRequired` is true, the
           * user will be directed to that checkout step via the `pushNextCheckoutState` thunk, submitted at each
           * step's submit handler.
           *
           * It's not shown in this UI to prevent confusion and to prevent users from attempting to navigate back to
           * the flow to trigger another registration event and accidentally activating MFA.
           */}
          {/* {accountSetupRequired && (
            <StylizedCardItemLinkWithIcon
              sectionTitle={'Account'}
              capsuleIcon={CAPSULE_ICON_STRINGS.checkout_account}
              to={paths.checkoutAccountSetup.toPath({ cartId })}
              ariaLabel={'Add Username and Password'}
              textList={[currentUserEmail, '*****']}
              isDisabled={
                !bffCartDeliveryAddress ||
                isEmpty(bffCartDeliveryMethod) ||
                (needsCreditCard && !paymentCardId) ||
                isAccountSet
              }
              disabledText={currentUserEmail ?? 'Create your account'}
            />
          )} */}
        </Card>
        <CheckoutPriceBreakdownCard
          onEditClicked={handleOnEditClicked}
          setIsUserEditingTip={setIsUserEditingTip}
          isUserEditingTip={isUserEditingTip}
          showTip={isTippingEnabled}
          total={total}
          tip={tip}
          showError={shouldFinishEditingTip}
          onTipSubmitted={handleOnTipSubmitted}
          isDisabled={!showPrice}
        />
        {/* We did not show any pickup/signature requirements for ESI orders. */}
        {showSignatureOrPickupRequirement && (
          <Card>
            <CardItem>
              <span className={hasError && !fieldValidity['signature'] ? styles.errorText : 'label'}>
                {hasError && !fieldValidity['signature'] ? validationErrors['signature'] : signatureRequirementTitle}
              </span>
              <CheckBoxInputUnwired
                className={styles.textSpacer}
                alternateLabelStyle
                onChange={({ target: { checked } }) => {
                  dispatchSignatureRequirementChecked(checked);
                  toggleSignatureCheck();
                  setHasError(false);
                }}
                checked={signatureRequirementChecked}
                label={
                  hasSelectedPickupStationFromRadioGroup
                    ? 'I acknowledge that I work at this office location and can pick up my order at the health center.'
                    : shouldRequireSignature
                    ? 'I acknowledge that someone will be available to receive and sign for my order.'
                    : 'I acknowledge that someone will be available to receive my order.'
                }
                data-cy="signature_checkbox"
                name={shouldRequireSignature ? `signature_requirement` : `delivery_requirement`}
              />
            </CardItem>
          </Card>
        )}
        <FixedFloat>
          <Button
            variant="contained-primary-lg"
            onClick={completeOrder}
            isFullWidth
            isSubmitting={isSubmitting}
            data-cy={'checkout-place-order'}
            css={css`
              margin-bottom: ${({ theme }) => theme.space.s5};

              ${({ theme }) => theme.mediaQueries.mediumDown} {
                margin-left: auto;
                margin-right: auto;
                max-width: calc(100% - ${({ theme }) => theme.space.s6});
              }
            `}
          >
            Place Order
          </Button>
        </FixedFloat>
      </div>
    </StandardPage>
  );
};

export default Checkout;
